import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function WebsiteDevelopment() {
  const features = [
    {
      icon: 'ri-smartphone-line',
      title: 'Responsive Design',
      description: 'Your website will look perfect on all devices - desktop, tablet, and mobile.'
    },
    {
      icon: 'ri-speed-line',
      title: 'Lightning Fast',
      description: 'Optimized for speed with 90+ PageSpeed scores and instant loading times.'
    },
    {
      icon: 'ri-search-eye-line',
      title: 'SEO Ready',
      description: 'Built with SEO best practices to help you rank higher on Google.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Secure & Reliable',
      description: 'SSL certificates, security headers, and regular updates included.'
    },
    {
      icon: 'ri-edit-box-line',
      title: 'Easy to Update',
      description: 'Simple content management system that you can update yourself.'
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Ongoing Support',
      description: '30 days of free support and training to get you started.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery Call',
      description: 'We discuss your business goals, target audience, and design preferences.',
      duration: '30 minutes'
    },
    {
      step: '02',
      title: 'AI Design Generation',
      description: 'Our AI creates multiple design concepts based on your requirements.',
      duration: '2-4 hours'
    },
    {
      step: '03',
      title: 'Development',
      description: 'We build your website with clean code and modern technologies.',
      duration: '12-24 hours'
    },
    {
      step: '04',
      title: 'Review & Launch',
      description: 'Final review, testing, and deployment to your domain.',
      duration: '2-4 hours'
    }
  ];

  const packages = [
    {
      name: 'Starter',
      price: '$999',
      description: 'Perfect for small businesses and startups',
      features: [
        'Up to 5 pages',
        'Responsive design',
        'Contact form',
        'SEO optimization',
        'SSL certificate',
        '30 days support'
      ],
      popular: false
    },
    {
      name: 'Professional',
      price: '$1,499',
      description: 'Ideal for growing businesses',
      features: [
        'Up to 10 pages',
        'Advanced animations',
        'Blog integration',
        'Google Analytics',
        'Social media integration',
        '60 days support'
      ],
      popular: true
    },
    {
      name: 'Enterprise',
      price: '$2,499',
      description: 'For established businesses',
      features: [
        'Unlimited pages',
        'Custom functionality',
        'Advanced integrations',
        'Priority support',
        'Performance optimization',
        '90 days support'
      ],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 brand-soft-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Custom Website
                <span className="block brand-heading-accent">
                  Development
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Get a professional, AI-powered website that converts visitors into customers. 
                Built with modern technology and delivered in 24-48 hours.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/vibe-check"
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap text-center"
                >
                  Start Your Project
                </Link>
                <Link
                  to="/contact"
                  className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-600 hover:text-white transition-colors whitespace-nowrap text-center"
                >
                  Get Free Quote
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="/site-images/website-development-hero.webp"
                alt="Website Development Process"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Our Development Service?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine 15 years of experience with cutting-edge AI technology to deliver exceptional websites faster and more affordably.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-blue-600 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Development Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From concept to launch in just 24-48 hours with our streamlined AI-powered process.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-8 shadow-lg text-center">
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-600 mb-4">{step.description}</p>
                  <span className="text-sm text-blue-600 font-semibold">{step.duration}</span>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <i className="ri-arrow-right-line text-blue-600 text-2xl"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Package</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transparent pricing with no hidden fees. All packages include hosting setup and SSL certificate.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div key={index} className={`relative bg-white rounded-2xl shadow-lg overflow-hidden ${pkg.popular ? 'ring-2 ring-blue-600 transform scale-105' : ''}`}>
                {pkg.popular && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-b-lg text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                  <p className="text-gray-600 mb-6">{pkg.description}</p>
                  <div className="text-4xl font-bold text-blue-600 mb-8">{pkg.price}</div>
                  <ul className="space-y-4 mb-8">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-600">
                        <i className="ri-check-line text-green-500 mr-3"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/vibe-check"
                    className={`w-full block text-center py-3 px-6 rounded-lg font-semibold transition-colors whitespace-nowrap ${
                      pkg.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white'
                    }`}
                  >
                    Get Started
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 brand-cta-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Launch Your Website?</h2>
          <p className="text-xl brand-cta-copy mb-8">
            Join hundreds of satisfied clients who've transformed their online presence with NeuraSites AI.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/vibe-check"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Start Your Project
            </Link>
            <Link
              to="/portfolio"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-[#0F2A4E] transition-colors whitespace-nowrap"
            >
              View Our Work
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
